import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import TicketCard from 'src/common/components/styled/TicketCard';
import { GENDERS_BY_NAME } from 'src/common/constants/genders';
import { minutesToHours } from 'src/helpers/dateTimeFormatters';
import { IAnonymizedCheckin } from 'src/interfaces/Checkin';
import { TStatistic } from 'src/interfaces/Statistics';

interface Props {
  statistic: TStatistic;
}

const PODIUM_PLACE_BG: Record<1 | 2 | 3, string> = {
  1: '#F7EDCF',
  2: '#ECEFF3',
  3: '#EDE0D2',
};

const PODIUM_PLACE_BORDER: Record<1 | 2 | 3, string> = {
  1: '#E8D4A8',
  2: '#D4D8DE',
  3: '#D9C4AE',
};

function formatNames(entries: IAnonymizedCheckin[]) {
  return entries.map((w, i) => `${w.name}${i + 1 < entries.length ? ', ' : ''}`).join('');
}

function podiumCardSx(place: 1 | 2 | 3, enlarged = false): SxProps<Theme> {
  return {
    opacity: 1,
    bgcolor: PODIUM_PLACE_BG[place],
    borderColor: PODIUM_PLACE_BORDER[place],
    ...(enlarged
      ? {
          '& .MuiTypography-h5': {
            typography: 'h4',
            fontWeight: 600,
          },
        }
      : {}),
  };
}

function PodiumPlaceCard({
  place,
  entries,
  enlarged = false,
}: {
  place: 1 | 2 | 3;
  entries: IAnonymizedCheckin[];
  enlarged?: boolean;
}) {
  const { t } = useTranslation();
  if (entries.length === 0) return null;

  const timeLabel = minutesToHours(entries[0]?.duration ?? 0, t);

  return (
    <TicketCard
      eyebrow={t('insights.podium.place', { place })}
      title={formatNames(entries)}
      description={timeLabel}
      sx={podiumCardSx(place, enlarged)}
      innerSx={{ mt: enlarged ? 2 : 1.5, py: enlarged ? 2.5 : 2 }}
    />
  );
}

function PodiumGenderBlock({
  genderKey,
  winners,
}: {
  genderKey: string;
  winners: TStatistic['winnersByGender'][keyof TStatistic['winnersByGender']];
}) {
  const { t } = useTranslation();
  const gender = t(`base.gender.${GENDERS_BY_NAME[genderKey]?.name ?? genderKey}`);

  const hasAnyWinner =
    winners.firstPlace.length > 0 ||
    winners.secondPlace.length > 0 ||
    winners.thirdPlace.length > 0;

  if (!hasAnyWinner) {
    return null;
  }

  return (
    <Grid size={{ xs: 12, md: 6 }}>
      <Typography variant="subtitle1" component="h3" sx={{ mb: 1 }}>
        {t('statistics.winners', { gender })}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {winners.firstPlace.length > 0 ? (
          <PodiumPlaceCard place={1} entries={winners.firstPlace} enlarged />
        ) : null}
        {winners.secondPlace.length > 0 || winners.thirdPlace.length > 0 ? (
          <Grid container spacing={1}>
            {winners.secondPlace.length > 0 ? (
              <Grid size={{ xs: 12, sm: 6 }}>
                <PodiumPlaceCard place={2} entries={winners.secondPlace} />
              </Grid>
            ) : null}
            {winners.thirdPlace.length > 0 ? (
              <Grid size={{ xs: 12, sm: 6 }}>
                <PodiumPlaceCard place={3} entries={winners.thirdPlace} />
              </Grid>
            ) : null}
          </Grid>
        ) : null}
      </Box>
    </Grid>
  );
}

export default function PodiumSection({ statistic }: Props) {
  const { checkinsByGenderByAgeKeys, winnersByGender } = statistic;

  return (
    <Grid container spacing={2}>
      {checkinsByGenderByAgeKeys.map(k => (
        <PodiumGenderBlock key={k} genderKey={k} winners={winnersByGender[k]} />
      ))}
    </Grid>
  );
}
